import type { AdminRecentSignup, SignupRiskFlag } from '@pulsify/shared/dto'
import { AlertTriangle, Eye, EyeOff, ShieldCheck, UserPlus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAdminRecentSignups } from '~/entities/stats/model/use-admin-stats'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'

const FLAG_LABELS: Record<SignupRiskFlag, () => string> = {
  burst_signup: m.signupsFlagBurst,
  shared_email_domain: m.signupsFlagSharedDomain,
  unconfirmed_email: m.signupsFlagUnconfirmed,
  duplicate_display_name: m.signupsFlagDuplicateName,
  no_date_of_birth: m.signupsFlagNoDob,
  date_of_birth_changed: m.signupsFlagDobChanged,
  no_avatar: m.signupsFlagNoAvatar,
}

/** Flags that say something is off, as opposed to merely incomplete. */
const STRONG_FLAGS = new Set<SignupRiskFlag>([
  'burst_signup',
  'shared_email_domain',
  'duplicate_display_name',
])

const WINDOWS = [7, 30, 90] as const
type Window = (typeof WINDOWS)[number]

const WINDOW_LABELS: Record<Window, () => string> = {
  7: m.geoWindow7,
  30: m.geoWindow30,
  90: m.geoWindow90,
}

const isStrong = (signup: AdminRecentSignup) =>
  signup.riskFlags.some((flag) => STRONG_FLAGS.has(flag))

function maskEmail(email: string): string {
  const at = email.lastIndexOf('@')
  if (at <= 0) return '•••'
  return `${email.slice(0, 1)}•••${email.slice(at)}`
}

function EmailCell({ email }: { email: string | null }) {
  const [revealed, setRevealed] = useState(false)
  if (!email) {
    return <span className="text-muted-foreground">{m.signupsNoEmail()}</span>
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono text-xs">{revealed ? email : maskEmail(email)}</span>
      <Button
        variant="ghost"
        size="sm"
        className="px-1 h-6"
        onClick={() => setRevealed((value) => !value)}
        aria-label={revealed ? m.signupsHide() : m.signupsReveal()}
      >
        {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </Button>
    </span>
  )
}

function SummaryTile({
  label,
  value,
  detail,
  alert = false,
}: {
  label: string
  value: string
  detail?: string
  alert?: boolean
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-muted-foreground text-sm">{label}</p>
        <p className={cn('mt-1 font-semibold text-2xl tabular-nums', alert && 'text-destructive')}>
          {value}
        </p>
        {detail ? <p className="mt-1 text-muted-foreground text-xs">{detail}</p> : null}
      </CardContent>
    </Card>
  )
}

function SignupRow({ signup }: { signup: AdminRecentSignup }) {
  const locale = getLocale()
  return (
    <tr className="border-border border-b last:border-0">
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          {isStrong(signup) ? (
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0" aria-hidden />
          ) : null}
          <span className="font-medium">{signup.displayName}</span>
          {signup.isGuest ? (
            <Badge variant="outline" className="text-xs">
              {m.signupsGuest()}
            </Badge>
          ) : null}
        </div>
      </td>
      <td className="py-3 pr-4">
        <EmailCell email={signup.email} />
      </td>
      <td className="py-3 pr-4 tabular-nums">
        {signup.age === null ? <span className="text-muted-foreground">-</span> : signup.age}
      </td>
      <td className="py-3 pr-4">{signup.country ?? '-'}</td>
      <td className="py-3 pr-4 text-muted-foreground text-sm">{signup.provider ?? '-'}</td>
      <td className="py-3 pr-4 text-muted-foreground text-sm whitespace-nowrap">
        {new Date(signup.createdAt).toLocaleString(locale, {
          dateStyle: 'short',
          timeStyle: 'short',
        })}
      </td>
      <td className="py-3">
        <div className="flex flex-wrap gap-1">
          {signup.riskFlags.map((flag) => (
            <Badge
              key={flag}
              variant={STRONG_FLAGS.has(flag) ? 'destructive' : 'secondary'}
              className="text-xs"
            >
              {FLAG_LABELS[flag]()}
            </Badge>
          ))}
        </div>
      </td>
    </tr>
  )
}

export function SignupsPage() {
  const [days, setDays] = useState<Window>(7)
  const [onlyFlagged, setOnlyFlagged] = useState(false)
  const query = useAdminRecentSignups({ days, limit: 200 })
  const locale = getLocale()
  const count = (value: number) => new Intl.NumberFormat(locale).format(value)

  const signups = query.data?.signups ?? []
  const flaggedCount = useMemo(() => signups.filter(isStrong).length, [signups])
  const rows = useMemo(
    () => (onlyFlagged ? signups.filter(isStrong) : signups),
    [signups, onlyFlagged],
  )
  const ageStats = query.data?.ageStats

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-16">
      <div className="space-y-8 mx-auto max-w-6xl">
        <header className="flex flex-wrap justify-between items-end gap-x-6 gap-y-4">
          <div className="space-y-1.5">
            <h1 className="flex items-center gap-2.5 font-semibold text-foreground text-3xl tracking-tight">
              <UserPlus className="w-7 h-7" aria-hidden />
              {m.signupsTitle()}
            </h1>
            <p className="text-muted-foreground text-sm">{m.signupsSubtitle()}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              label={m.signupsWindowLabel()}
              value={String(days)}
              onChange={(value) => setDays(Number(value) as Window)}
              options={WINDOWS.map((window) => ({
                value: String(window),
                label: WINDOW_LABELS[window](),
              }))}
            />
            <SegmentedControl
              label={m.signupsFilterLabel()}
              value={onlyFlagged ? 'flagged' : 'all'}
              onChange={(value) => setOnlyFlagged(value === 'flagged')}
              options={[
                { value: 'all', label: m.signupsAll() },
                { value: 'flagged', label: m.signupsOnlyFlagged() },
              ]}
            />
          </div>
        </header>

        <div className="gap-4 grid sm:grid-cols-3">
          <SummaryTile
            label={m.signupsWindowTotal()}
            value={query.data ? count(query.data.windowTotal) : '-'}
          />
          <SummaryTile
            label={m.signupsAverageAge()}
            value={ageStats?.averageAge == null ? '-' : ageStats.averageAge.toFixed(1)}
            detail={
              ageStats && ageStats.withDateOfBirth > 0 && query.data
                ? m.signupsAgeCoverage({
                    withDob: count(ageStats.withDateOfBirth),
                    total: count(query.data.windowTotal),
                  })
                : m.signupsNoAge()
            }
          />
          <SummaryTile
            label={m.signupsFlagged()}
            value={query.data ? count(flaggedCount) : '-'}
            detail={query.data && flaggedCount === 0 ? m.signupsNoneFlagged() : undefined}
            alert={flaggedCount > 0}
          />
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {query.isLoading ? (
              <div className="space-y-3 p-6">
                {['a', 'b', 'c', 'd', 'e', 'f'].map((placeholder) => (
                  <Skeleton key={placeholder} className="w-full h-10" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 p-12 text-center">
                <ShieldCheck className="w-8 h-8 text-muted-foreground" aria-hidden />
                <p className="text-muted-foreground text-sm">
                  {onlyFlagged ? m.signupsNoneFlagged() : m.signupsEmpty()}
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                    <th className="py-3 pr-4 pl-4 font-medium">{m.signupsColumnPerson()}</th>
                    <th className="py-3 pr-4 font-medium">{m.signupsColumnEmail()}</th>
                    <th className="py-3 pr-4 font-medium">{m.signupsColumnAge()}</th>
                    <th className="py-3 pr-4 font-medium">{m.signupsColumnCountry()}</th>
                    <th className="py-3 pr-4 font-medium">{m.signupsColumnProvider()}</th>
                    <th className="py-3 pr-4 font-medium">{m.signupsColumnCreated()}</th>
                    <th className="py-3 pr-4 font-medium">{m.signupsColumnFlags()}</th>
                  </tr>
                </thead>
                <tbody className="[&_td:first-child]:pl-4">
                  {rows.map((signup) => (
                    <SignupRow key={signup.profileId} signup={signup} />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
